import apiFetch from '@/utils/apiFetch';
import { defineStore } from 'pinia';
import DefaultConfig from '@/config/default';
import type { Permission } from '@/config/types/responses';
import type { Pagination } from '@/config/types/pagination';

const usePermissionStore = defineStore('permissions', {
  state: () => ({
    loading: false,
    error: null as string | null,
    permissions: [] as Permission[],
    userPermissions: [] as Permission[],
    pagination: {} as Pagination,
  }),

  actions: {
    async fetchPermissions() {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/public/users/permissions`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error fetching permissions');
        }
        const data = await res.json();
        this.permissions = Array.isArray(data.data) ? data.data : (data.data?.results || []);
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async fetchAdminPermissions() {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/admin/users/permissions`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error fetching permissions');
        }
        const data = await res.json();
        this.permissions = Array.isArray(data.data) ? data.data : (data.data?.results || []);
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async fetchUserPermissions(userId: string) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/public/users/permissions/${userId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error fetching user permissions');
        }
        const data = await res.json();
        this.userPermissions = Array.isArray(data.data) ? data.data : (data.data?.results || []);
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async fetchAdminUserPermissions(userId: string) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/admin/users/permissions/${userId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error fetching user permissions');
        }
        const data = await res.json();
        this.userPermissions = Array.isArray(data.data) ? data.data : (data.data?.results || []);
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async assignAdminPermission(userId: string, permissionId: string) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/admin/users/permissions/assign`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, permissionId }),
        });
        if (!res.ok) {
          throw new Error('Error assigning permission');
        }
        await this.fetchAdminUserPermissions(userId);
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async assignPermission(userId: string, permissionId: string) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/public/users/permissions/assign`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, permissionId }),
        });
        if (!res.ok) {
          throw new Error('Error assigning permission');
        }
        await this.fetchUserPermissions(userId);
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async removePermission(userId: string, permissionId: string) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/public/users/permissions/unassign`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, permissionId }),
        });
        if (!res.ok) {
          throw new Error('Error removing permission');
        }
        await this.fetchUserPermissions(userId);
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async removeAdminPermission(userId: string, permissionId: string) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/admin/users/permissions/remove`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, permissionId }),
        });
        if (!res.ok) {
          throw new Error('Error removing permission');
        }
        await this.fetchAdminUserPermissions(userId);
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },
  },
});

export { usePermissionStore };
export default usePermissionStore;
